<template>
  <div>
    <ol class="steps">
      <li
        v-for="(step, index) in steps"
        :key="index"
        :class="{ 'step--active': index === currentStep && !isFinished, 'step--passed': index < currentStep }"
        class="step"
        @click="() => skipToPreviousStep(index)"
      >
        <div
          class="step__content"
          :class="{ 'error--current': (step.hasErrors ?? false) && index === currentStep, 'error--future': (step.hasErrors ?? false) && index !== currentStep }"
        >
          <slot
            name="step-content"
            :step="step"
            :index="index"
          >
            {{ step.label }}
          </slot>
        </div>
      </li>
      <li
        v-if="isFinished"
        :class="{ 'step--active': isFinished }"
        class="step"
      >
        <div class="step__content">
          {{ trans('workflow.finished') }}
        </div>
      </li>
    </ol>

    <div
      v-for="(step, index) in steps"
      :key="step.id"
    >
      <template v-if="isActiveStep(step.id)">
        <slot
          :name="step.id"
          :change-step="changeStep"
          :props="getProps(index)"
        />
      </template>
    </div>
  </div>
</template>

<script>
/**
 * The Stepper displays an arrow lint on top and loads in any pages from its given slots (steps) in progression
 * When going to another step, a step receives a stepIndex variable and changeStep function through its slot parameters
 * When stepIndex changes, the watching step should then perform a finishing action, such as save, and call changeStep
 */
export default {
  name: 'VStepper',
  props: {
    steps: {
      required: true,
      type: Array,
    },
    startStep: {
      required: false,
      type: Number,
      default: 0,
    },
  },

  data: function () {
    return {
      currentStep: 0,
      stepProps: {},
    }
  },

  computed: {
    isFinished () {
      return this.currentStep >= this.steps.length
    },
  },

  mounted () {
    this.currentStep = this.startStep
  },

  methods: {
    changeStep (stepIndex, props) {
      this.currentStep = stepIndex

      // Can be set to null, but not undefined
      // This is to allow changeStep to be called without props parameter, in which case stepProps should not be changed
      if (props !== undefined) {
        this.stepProps[this.currentStep] = props || null
      }
    },

    skipToPreviousStep (stepId) {
      if (stepId < this.currentStep) {
        this.changeStep(stepId)
      }
    },

    isActiveStep (stepId) {
      return this.steps[this.currentStep]?.id == stepId
    },

    getProps (index) {
      return this.stepProps[index]
    },
  },
}
</script>

<style lang="scss" scoped>
.steps {
  padding: 0;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  list-style-type: none;
  margin: 0 0 24px;

  &:hover {
    .step--active {
      flex-shrink: 1; // let the active step shrink if another step is hovered
    }
  }

  .step--passed {
    background-color: #a9aebb !important;
    cursor: pointer !important;
  }

  .step--passed:after {
    border-left-color: #a9aebb !important;
    cursor: pointer !important;
  }

  .error--current {
    color: #ff8793;
  }

  .error--future {
    color: #dc3545;
  }

  .step {
    background-color: #fff;
    flex: 1 1 auto;
    position: relative;
    padding: 10px 0 10px 35px;
    margin-right: 10px;
    text-align: center;
    height: 48px;
    min-width: 0;
    display: flex;
    justify-content: center;
    align-content: center;
    flex-direction: column;
    transition: flex-shrink 0.1s;
    cursor: default;

    &__content {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      font-weight: 600;
      text-transform: uppercase;
      font-size: 0.9em;
    }

    &:hover {
      flex-shrink: 0;
      transition: flex-shrink 0.2s;
    }

    &:before {
      width: 0;
      height: 0;
      border-top: 24px inset transparent;
      border-bottom: 24px inset transparent;
      border-left: 24px solid #e7eeee;
      position: absolute;
      content: "";
      top: 0;
      left: 0;
    }

    &:after {
      width: 0;
      height: 0;
      border-top: 24px inset transparent;
      border-bottom: 24px inset transparent;
      border-left: 24px solid #fff;
      position: absolute;
      content: "";
      top: 0;
      right: -24px;
      z-index: 2;
    }

    &:first-child {
      border-radius: 4px 0 0 4px;
      padding-left: 13px;
    }

    &:last-child {
      border-radius: 0 4px 4px 0;
      margin-right: 0;
      padding-right: 13px;
    }

    &:first-child:before,
    &:last-child:after {
      border: none;
    }

    &:first-child .badge {
      margin-left: 0;
    }

    &--active {
      background-color: #646B78;
      color: #ffffff;
      flex-shrink: 0;

      &:after {
        border-left-color: #646B78;
      }
    }
  }
}
</style>
